import { hrApiClient } from '@/lib/api';
import { IFilter, IItemResponse, IListResponse, IOptions } from '@/shared';
import { InsightDataDto } from '@/shared/job/insight.dto';
import {
  AutocompleteResponseDto,
  BannerAdsHistoryDto,
  CheckSkillCompatibilityResponseDto,
  PublicJobDetailDTO,
  PublicJobListDTO,
  TopProfessionDto,
} from '@/shared/job/job.public.dto';
import type {
  ZangiaPlusAdditionalKey,
  ZangiaPlusReportDTO,
} from '@/shared/user/dashboard';

export async function getSavedJobs(
  params: IFilter = {}
): Promise<IListResponse<PublicJobListDTO>> {
  const { data } = await hrApiClient.get('/user/job/saved', { params });
  return data;
}
export async function getSavedJobIds(): Promise<number[]> {
  const { data } = await hrApiClient.get<IItemResponse<Array<number | string>>>(
    '/user/job/saved-ids'
  );
  const raw = Array.isArray(data) ? data : (data?.data ?? []);
  if (!Array.isArray(raw)) return [];
  return raw.map(Number).filter((id) => Number.isFinite(id));
}

export async function saveJob(jobId: number): Promise<IItemResponse> {
  const { data } = await hrApiClient.post(`/user/job/save/${jobId}`);
  return data;
}

export async function getJobDetail(
  code: string | number
): Promise<PublicJobDetailDTO> {
  const { data } = await hrApiClient.get(`/job/public/detail/${code}`);
  return data.data;
}

export async function getCompanyJobList(
  companyId: number
): Promise<IListResponse<PublicJobListDTO>> {
  const { data } = await hrApiClient.get<IListResponse<PublicJobListDTO>>(
    `/job/public/list/company/${companyId}`
  );
  return data;
}

export async function getTopCountWorks(): Promise<
  IListResponse<TopProfessionDto>
> {
  const { data } = await hrApiClient.get<IListResponse<TopProfessionDto>>(
    '/job/public/top-professions'
  );
  return data;
}

export async function getActiveProfessions(
  branchId?: number
): Promise<IListResponse<IOptions>> {
  const { data } = await hrApiClient.get<IListResponse<IOptions>>(
    '/job/public/professions',
    { params: branchId ? { branchId } : undefined }
  );
  return data;
}

export async function searchJobs(
  params: IFilter = {}
): Promise<IListResponse<PublicJobListDTO>> {
  const { data } = await hrApiClient.get<IListResponse<PublicJobListDTO>>(
    '/job/public/list/search',
    { params }
  );
  return data;
}

export async function getBannerJobList(): Promise<
  IListResponse<BannerAdsHistoryDto>
> {
  const { data } = await hrApiClient.get<IListResponse<BannerAdsHistoryDto>>(
    '/job/public/banner'
  );
  return data;
}

export async function setBannerJobClickCount(id: number): Promise<number> {
  const { data } = await hrApiClient.post<IItemResponse<number>>(
    `/job/public/banner/click/${id}`
  );
  return data.data ?? 0;
}

export async function getRelatedJobs(
  jobId: number | string
): Promise<IListResponse<PublicJobListDTO>> {
  const { data } = await hrApiClient.get<IListResponse<PublicJobListDTO>>(
    `/job/public/related/${jobId}`
  );
  return data;
}

export async function increaseShareCount(id: number): Promise<number> {
  const { data } = await hrApiClient.post<IItemResponse<number>>(
    `/job/public/share/${id}`
  );
  return data.data ?? 0;
}

export async function sendCV(id: number): Promise<boolean> {
  const { data } = await hrApiClient.post<IItemResponse<boolean>>(
    `/applicants/send/${id}`
  );
  if (data?.data === false) return false;
  return true;
}

export async function checkSkillCompatibility(
  jobId: number,
  langId?: number
): Promise<CheckSkillCompatibilityResponseDto> {
  const { data } = await hrApiClient.post<
    IItemResponse<CheckSkillCompatibilityResponseDto>
  >('/job/public/check-skill-compatibility', {
    job_id: jobId,
    lang_id: langId ?? 1,
  });
  return data.data as CheckSkillCompatibilityResponseDto;
}

export async function getSkillCompatibility(
  jobId: number
): Promise<CheckSkillCompatibilityResponseDto | null> {
  const { data } = await hrApiClient.get<
    IItemResponse<CheckSkillCompatibilityResponseDto>
  >('/job/public/get-skill-compatibility', {
    params: { job_id: jobId },
  });
  return data.data ?? null;
}

export async function getSuggestedJobList(): Promise<
  (PublicJobListDTO & { matchLevel?: number })[]
> {
  const { data } = await hrApiClient.get<
    IListResponse<PublicJobListDTO & { matchLevel?: number }>
  >('/job/public/suggested');
  return data.data ?? [];
}

export async function getJobAutocomplete(
  query: string
): Promise<AutocompleteResponseDto[]> {
  const { data } = await hrApiClient.get<AutocompleteResponseDto[]>(
    '/job/public/autocomplete',
    { params: { query: query.trim() } }
  );
  return data ?? [];
}
export async function fetchHomeSearch(query: string): Promise<string[]> {
  const { data } = await hrApiClient.get<string[]>('/job/public/homesearch', {
    params: { query: query.trim() },
  });
  return data ?? [];
}

export async function getJobInsight(
  jobId: number
): Promise<IItemResponse<InsightDataDto>> {
  const { data } = await hrApiClient.get<IItemResponse<InsightDataDto>>(
    `/applicants/insight-data/${jobId}`
  );
  return data;
}

export type ActivateZangiaPlusPayload = {
  itemId: number;
  additional: Array<'cvtop' | 'suggesttop'>;
};
export async function activateZangiaPlus(
  payload: ActivateZangiaPlusPayload
): Promise<IItemResponse> {
  const { data } = await hrApiClient.post<IItemResponse>(
    '/user/buy-zangia-plus',
    payload
  );
  return data;
}

export async function getZangiaPlusReport(): Promise<ZangiaPlusReportDTO> {
  const { data } = await hrApiClient.get<ZangiaPlusReportDTO>(
    '/user/zangia-plus/report'
  );
  return data;
}

export async function updateZangiaPlusAdditional(
  keys: ZangiaPlusAdditionalKey[]
): Promise<boolean> {
  const { data } = await hrApiClient.post<boolean>(
    '/user/zangia-plus/additional',
    { keys }
  );
  return data;
}
